import React, {useState, useContext} from 'react';
import {Input, Select, Button, Row, Col, Popconfirm, Form, Tooltip} from "antd";
import dayjs from "dayjs";
import {internalApi} from "../../common/axios";
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import {DIALOG_KEY, onOffList, couponTypeList} from "../../shared/utils/enum";
import SearchDropDown from "../../shared/components/common/SearchDropDown";
import CustomDateTimePicker from "../../shared/components/date/CustomDateTimePicker";
import toast from "react-hot-toast";
import {isJSON} from "../../shared/utils/basic";
import {AuthContext} from "../../shared/contexts/authContext";
import {buildUrl} from "../../shared/utils/net";
import {CouponEntity} from "./CouponEntity";
import IsLoading from "../../shared/components/IsLoading";
import ErrorPage from "../../shared/components/ErrorPage";
import {DeleteOutlined, ExclamationCircleOutlined} from '@ant-design/icons';
import {useItemKrTextData} from "../probability/useItemData";

const { Option } = Select;
dayjs.extend(utc);
dayjs.extend(timezone);

const CouponEditModal = ({record, handleClose, dialogRef, handleSetDialog}) => {
    const data = new CouponEntity(record);
    const [rows, setRows] = useState(data);
    const [saving, setSaving] = useState(false);
    const [items, setItems] = useState(() => isJSON(rows.items) ? JSON.parse(rows.items) : rows.items);
    const {currentUser} = useContext(AuthContext);
    const { data: krItemTextData, isLoading, error } = useItemKrTextData();

    const handleSetRows = (key, value) => {
        setRows((prevState) => {
            const updatedData = { ...prevState, [key]: value };
            return new CouponEntity(updatedData);
        });
    }

    const handleDelete = async () => {
        const currentDialog = dialogRef.current;
        if (currentDialog.confirmText === 'DELETE') {
            await internalApi.delete(buildUrl('coupon/coupon_master', {couponId: rows.id})).then((res) => {
                toast.success(res.data.message);
                handleClose(true);
            }).catch((err) => {
                toast.error("삭제 실패");
                console.error(err);
            });
            handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ""}]);
        } else {
            toast.error("잘못된 확인 문자입니다. DELETE 를 입력하세요.");
            handleSetDialog([{key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
        }
    }

    const handleSave = async () => {
        rows.items = items;
        if (saving || rows.isInvalidate()) return;
        setSaving(true);
        await internalApi.post('coupon/coupon_master', {data: rows.toJson()}).then((res) => {
            toast.success(res.data.message);
            handleClose(true);
        }).catch((err) => {
            console.error(err);
            toast.error("저장 실패");
        }).finally(() => setSaving(false));
        handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ""}]);
    }

    if (isLoading) return <IsLoading />;
    if (error) return <ErrorPage error={error} />;
    return (
        <Form layout="vertical" onFinish={handleSave} className="coupon-master-edit" style={{ maxHeight: '80vh', overflowY: 'auto', padding: 8, marginTop: 24 }}>
            <Row gutter={16} className="coupon-master-edit-param">
                <Col span={8}>
                    <Form.Item label="쿠폰 이름" required>
                        <Input
                            value={rows.id}
                            placeholder="쿠폰이름을 입력하세요.(**자 이내)"
                            onChange={e => handleSetRows('id', e.target.value)}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    {rows.type > 0 ? (
                        <Form.Item label="쿠폰 수량" required>
                            <Input
                                type="number"
                                value={rows.seed}
                                onChange={e => handleSetRows('seed', e.target.value)}
                            />
                        </Form.Item>
                    ) : (
                        <Form.Item label="발급할 쿠폰 아이디" required>
                            <Input
                                value={rows.seedId}
                                onChange={e => handleSetRows('seedId', e.target.value)}
                            />
                        </Form.Item>
                    )}
                </Col>
                <Col span={8}>
                    <Form.Item label="쿠폰 타입" required>
                        <Select
                            value={rows.type}
                            onChange={v => handleSetRows('type', v)}
                        >
                            {couponTypeList.map((option) => (
                                <Option key={option.value} value={option.value}>{option.label}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16} className="coupon-master-edit-param">
                <Col span={8}>
                    <Form.Item label="ON/OFF 설정" required>
                        <Select
                            value={rows.onOff}
                            onChange={v => handleSetRows('onOff', v)}
                        >
                            {onOffList.map((option) => (
                                <Option key={option.value} value={option.value}>{option.label}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="우편 유효기간">
                        <Input
                            type="number"
                            value={rows.validPeriod}
                            onChange={e => handleSetRows('validPeriod', Number(e.target.value))}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        label={
                            <>
                                우편 타이틀
                                <Tooltip title="쿠폰의 우편 타이틀은 다국어 지원이 되지 않습니다. 특별한 경우에만 사용하세요." placement="right">
                                    <ExclamationCircleOutlined style={{ marginLeft: 6, color: '#faad14' }} />
                                </Tooltip>
                            </>
                        }
                    >
                        <Input
                            value={rows.title}
                            onChange={e => handleSetRows('title', e.target.value)}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row className="coupon-master-edit-param">
                <Col span={24}>
                    <Form.Item label="기간">
                        <CustomDateTimePicker rows={rows} handleSetRows={handleSetRows}/>
                    </Form.Item>
                </Col>
            </Row>
            <Row className="coupon-master-edit-param" style={{ marginTop: 16 }}>
                <Col span={24}>
                    <SearchDropDown row={rows} dataArr={krItemTextData[currentUser.target]} items={items} setItems={setItems} hasQuantity={true}/>
                </Col>
            </Row>
            <Row justify="end" gutter={8} className="coupon-master-buttons">
                <Col>
                    <Button type="primary" htmlType="submit" loading={saving}>저장</Button>
                </Col>
                <Col>
                    <Button onClick={handleClose}>취소</Button>
                </Col>
                <Col>
                    {rows.id !== '' && (
                        <Popconfirm
                            title="정말 삭제하시겠습니까? 삭제하려면 'DELETE'를 입력하세요."
                            onConfirm={handleDelete}
                            okText="삭제"
                            cancelText="취소"
                            icon={<DeleteOutlined style={{ color: 'red' }} />}
                            description={<Input onChange={e => handleSetDialog([{ key: DIALOG_KEY.CONFIRM_TEXT, value: e.target.value }])} placeholder="DELETE" />}
                        >
                            <Button danger icon={<DeleteOutlined />}>삭제</Button>
                        </Popconfirm>
                    )}
                </Col>
            </Row>
        </Form>
    );
}

export default CouponEditModal;
